import React, { useState, useEffect } from 'react';
import { get, post, put, del } from '../../utils/apiClient';
import { clearCacheKey } from '../../utils/cacheUtils';
import Base64Image from '../Base64Image';
import {
  AdminSectionTitle,
  AdminSectionText,
  AdminLoginLabel,
  AdminLoginInput,
  AdminLoginButton,
  AdminButton,
  AdminSaveButton,
} from '../../styles/AdminPageStyles';

const AdminHomePage = () => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(null);

  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoError, setLogoError] = useState(null);

  useEffect(() => {
    const loadPage = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await get('/home');
        setPageData(data);
      } catch (err) {
        setError(err && err.message ? err.message : 'Failed to load home page data.');
      } finally {
        setLoading(false);
      }
    };

    loadPage();
  }, []);

  const handleHeaderOrgChange = (e) => {
    const { value } = e.target;
    setPageData((prev) => ({
      ...(prev || {}),
      header: {
        ...((prev && prev.header) || {}),
        organizationName: value,
      },
    }));
  };

  const handleMottoChange = (e) => {
    const { value } = e.target;
    setPageData((prev) => ({
      ...(prev || {}),
      mottoText: value,
    }));
  };

  const updateHeroMessageField = (index, field, value) => {
    setPageData((prev) => {
      if (!prev) return prev;
      const messages = Array.isArray(prev.heroMessages) ? [...prev.heroMessages] : [];
      const existing = messages[index] || {};
      messages[index] = {
        ...existing,
        [field]: value,
      };
      return {
        ...prev,
        heroMessages: messages,
      };
    });
  };

  const addHeroMessage = () => {
    setPageData((prev) => {
      const next = { ...(prev || {}) };
      const messages = Array.isArray(next.heroMessages) ? [...next.heroMessages] : [];
      messages.push({ title: '', subtitle: '', body: '', order: messages.length + 1 });
      next.heroMessages = messages;
      return next;
    });
  };

  const removeHeroMessage = (index) => {
    setPageData((prev) => {
      if (!prev || !Array.isArray(prev.heroMessages)) return prev;
      const messages = prev.heroMessages.filter((_, i) => i !== index);
      return {
        ...prev,
        heroMessages: messages,
      };
    });
  };

  const handleSaveContent = async (e) => {
    e.preventDefault();
    if (!pageData) return;

    setSaving(true);
    setError(null);
    setSaveSuccess(null);

    try {
      const heroMessages = Array.isArray(pageData.heroMessages) ? pageData.heroMessages : [];

      const payload = {
        header: {
          ...(pageData.header || {}),
          organizationName:
            (pageData.header && pageData.header.organizationName) ||
            'Victorious Faith Ministries - Redemption Chapel',
        },
        mottoText: pageData.mottoText || '',
        heroMessages,
      };

      const updated = await put('/home', payload);
      setPageData(updated);
      clearCacheKey('home_data');
      setSaveSuccess('Home page hero content saved successfully.');
    } catch (err) {
      setError(err && err.message ? err.message : 'Failed to save home page content.');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (file) => {
    if (!file) return;

    setLogoUploading(true);
    setLogoError(null);

    try {
      const formData = new FormData();
      formData.append('image', file);
      await post('/home/header/logo', formData);

      const data = await get('/home');
      setPageData(data);
      clearCacheKey('home_data');
    } catch (err) {
      setLogoError(err && err.message ? err.message : 'Failed to upload logo.');
    } finally {
      setLogoUploading(false);
    }
  };

  const handleDeleteLogo = async () => {
    setLogoUploading(true);
    setLogoError(null);

    try {
      await del('/home/header/logo');
      const data = await get('/home');
      setPageData(data);
      clearCacheKey('home_data');
    } catch (err) {
      setLogoError(err && err.message ? err.message : 'Failed to delete logo.');
    } finally {
      setLogoUploading(false);
    }
  };

  const handleHeroUpload = async (fileList) => {
    if (!fileList || fileList.length === 0) return;

    setUploading(true);
    setUploadError(null);

    try {
      const files = Array.from(fileList);

      // Upload each selected file as a separate hero image
      // The backend currently keeps up to a limited number of heroImages
      for (const file of files) {
        const formData = new FormData();
        formData.append('image', file);
        await post('/home/hero-images/upload', formData);
      }

      const data = await get('/home');
      setPageData(data);
      clearCacheKey('home_data');
    } catch (err) {
      setUploadError(err && err.message ? err.message : 'Failed to upload hero image(s).');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteHeroImage = async (index) => {
    setUploadError(null);
    setUploading(true);

    try {
      const response = await del(`/home/hero-images/${index}`);

      if (response && Array.isArray(response.heroImages)) {
        setPageData((prev) => ({
          ...(prev || {}),
          heroImages: response.heroImages,
        }));
      } else {
        const data = await get('/home');
        setPageData(data);
      }

      clearCacheKey('home_data');
    } catch (err) {
      setUploadError(
        err && err.message ? err.message : 'Failed to delete hero image.',
      );
    } finally {
      setUploading(false);
    }
  };

  if (loading && !pageData) {
    return (
      <>
        <AdminSectionTitle>Home Page CMS</AdminSectionTitle>
        <AdminSectionText>Loading home page data...</AdminSectionText>
      </>
    );
  }

  const header = pageData && pageData.header ? pageData.header : null;
  const heroImages = pageData && Array.isArray(pageData.heroImages) ? pageData.heroImages : [];
  const mottoText = pageData ? pageData.mottoText : null;
  const heroMessages = pageData && Array.isArray(pageData.heroMessages) ? pageData.heroMessages : [];

  return (
    <>
      <AdminSectionTitle>Home Page CMS - Hero Content &amp; Slideshow</AdminSectionTitle>

      {logoError && (
        <AdminSectionText style={{ color: '#b71c1c' }}>{logoError}</AdminSectionText>
      )}
      <AdminSectionText style={{ marginBottom: 12 }}>
        Manage the hero text and slideshow images that appear on the public home page.
      </AdminSectionText>

      <form onSubmit={handleSaveContent}>
        <section style={{ marginBottom: 24, borderBottom: '1px solid #eee', paddingBottom: 16 }}>
        <AdminSectionTitle style={{ fontSize: '1rem', marginBottom: 8 }}>
          Header Logo
        </AdminSectionTitle>
        <AdminSectionText style={{ fontSize: '0.9rem', marginBottom: 12 }}>
          Upload a logo to be displayed in the website header.
        </AdminSectionText>

        {header && header.logo ? (
          <div style={{ marginBottom: 12 }}>
            <div style={{ marginBottom: 8 }}>
              <Base64Image
                image={header.logo}
                alt="Header Logo"
                style={{ maxHeight: 80, objectFit: 'contain', border: '1px solid #ddd', padding: 4 }}
              />
            </div>
            <AdminButton
              type="button"
              onClick={handleDeleteLogo}
              disabled={logoUploading}
            >
              {logoUploading ? 'Deleting...' : 'Remove Logo'}
            </AdminButton>
          </div>
        ) : (
          <div style={{ marginBottom: 12 }}>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                if (e.target.files && e.target.files.length > 0) {
                  handleLogoUpload(e.target.files[0]);
                  e.target.value = '';
                }
              }}
              disabled={logoUploading}
              style={{ fontSize: '0.9rem' }}
            />
            {logoUploading && <span style={{ marginLeft: 8 }}>Uploading...</span>}
          </div>
        )}
      </section>

        <div style={{ marginBottom: 24 }}>
          <AdminSectionTitle style={{ fontSize: '1rem', marginBottom: 8 }}>
            Hero Text Content
          </AdminSectionTitle>

        <div style={{ marginBottom: 12 }}>
          <AdminLoginLabel htmlFor="home-org-name">Organization Name</AdminLoginLabel>
          <AdminLoginInput
            id="home-org-name"
            value={(header && header.organizationName) || ''}
            onChange={handleHeaderOrgChange}
          />
        </div>

        <div style={{ marginBottom: 12 }}>
          <AdminLoginLabel htmlFor="home-motto">Motto Text</AdminLoginLabel>
          <textarea
            id="home-motto"
            rows={2}
            value={mottoText || ''}
            onChange={handleMottoChange}
            style={{ width: '100%', padding: 8, fontSize: '0.9rem' }}
          />
        </div>

        <AdminSectionTitle style={{ fontSize: '0.95rem', margin: '16px 0 8px' }}>
          Hero Messages (optional rotating text)
        </AdminSectionTitle>
        <AdminSectionText style={{ fontSize: '0.85rem', marginBottom: 8 }}>
          If you add hero messages, they will rotate on the left side of the hero section.
          Leave this list empty to show only the organization name and motto.
        </AdminSectionText>

        {heroMessages && heroMessages.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            {heroMessages.map((msg, index) => (
              <div
                key={index}
                style={{
                  border: '1px solid #ddd',
                  borderRadius: 8,
                  padding: 10,
                  marginBottom: 8,
                  fontSize: '0.9rem',
                }}
              >
                <div style={{ marginBottom: 6 }}>
                  <AdminLoginLabel htmlFor={`hero-msg-title-${index}`}>
                    Title
                  </AdminLoginLabel>
                  <AdminLoginInput
                    id={`hero-msg-title-${index}`}
                    value={msg.title || ''}
                    onChange={(e) => updateHeroMessageField(index, 'title', e.target.value)}
                  />
                </div>
                <div style={{ marginBottom: 6 }}>
                  <AdminLoginLabel htmlFor={`hero-msg-subtitle-${index}`}>
                    Subtitle (optional)
                  </AdminLoginLabel>
                  <AdminLoginInput
                    id={`hero-msg-subtitle-${index}`}
                    value={msg.subtitle || ''}
                    onChange={(e) => updateHeroMessageField(index, 'subtitle', e.target.value)}
                  />
                </div>
                <div style={{ marginBottom: 6 }}>
                  <AdminLoginLabel htmlFor={`hero-msg-body-${index}`}>
                    Body (optional)
                  </AdminLoginLabel>
                  <textarea
                    id={`hero-msg-body-${index}`}
                    rows={3}
                    value={msg.body || ''}
                    onChange={(e) => updateHeroMessageField(index, 'body', e.target.value)}
                    style={{ width: '100%', padding: 8, fontSize: '0.9rem' }}
                  />
                </div>
                <div style={{ marginBottom: 8 }}>
                  <AdminLoginLabel htmlFor={`hero-msg-order-${index}`}>
                    Display Order (optional)
                  </AdminLoginLabel>
                  <AdminLoginInput
                    id={`hero-msg-order-${index}`}
                    type="number"
                    value={typeof msg.order === 'number' ? msg.order : ''}
                    onChange={(e) =>
                      updateHeroMessageField(
                        index,
                        'order',
                        e.target.value === '' ? undefined : Number(e.target.value),
                      )
                    }
                  />
                </div>
                <AdminButton type="button" onClick={() => removeHeroMessage(index)}>
                  Remove Message
                </AdminButton>
              </div>
            ))}
          </div>
        )}

        <div style={{ marginBottom: 16 }}>
          <AdminButton type="button" onClick={addHeroMessage}>
            Add Hero Message
          </AdminButton>
        </div>

        </div>

        <section style={{ marginBottom: 24 }}>
        <AdminSectionTitle style={{ fontSize: '1rem', marginBottom: 8 }}>
          Current Hero Images
        </AdminSectionTitle>
        <AdminSectionText style={{ fontSize: '0.9rem', marginBottom: 8 }}>
          Total images:
          {' '}
          {heroImages.length}
        </AdminSectionText>

        {heroImages.length === 0 && (
          <AdminSectionText style={{ fontSize: '0.9rem' }}>
            No hero images have been uploaded yet. The site will fall back to text-only
            hero content until you upload images.
          </AdminSectionText>
        )}

        {heroImages.length > 0 && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
              gap: 12,
              marginBottom: 16,
            }}
          >
            {heroImages.map((image, index) => (
              <div
                key={image.filename || image.uploadedAt || index}
                style={{
                  border: '1px solid #ddd',
                  borderRadius: 8,
                  padding: 8,
                  fontSize: '0.85rem',
                }}
              >
                <div style={{ marginBottom: 6 }}>
                  <Base64Image
                    image={image}
                    alt={header && header.organizationName ? header.organizationName : 'Hero image'}
                    style={{ width: '100%', height: 90, objectFit: 'cover', borderRadius: 4 }}
                  />
                </div>
                <div>
                  <div>
                    <strong>#
                      {index + 1}
                    </strong>
                  </div>
                  {image.filename && (
                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {image.filename}
                    </div>
                  )}
                  <div style={{ marginTop: 8 }}>
                    <AdminButton
                      type="button"
                      onClick={() => handleDeleteHeroImage(index)}
                      disabled={uploading}
                    >
                      Remove Image
                    </AdminButton>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section>
        <AdminSectionTitle style={{ fontSize: '1rem', marginBottom: 8 }}>
          Upload Hero Images
        </AdminSectionTitle>
        <AdminSectionText style={{ fontSize: '0.9rem', marginBottom: 8 }}>
          Select one or more image files to add them to the hero slideshow.
          You can upload as many images as you like.
        </AdminSectionText>

        <div style={{ marginBottom: 12 }}>
          <AdminLoginLabel htmlFor="hero-images-upload">Hero Images</AdminLoginLabel>
          <input
            id="hero-images-upload"
            type="file"
            accept="image/*"
            multiple
            onChange={(e) => {
              if (e.target.files && e.target.files.length > 0) {
                handleHeroUpload(e.target.files);
                e.target.value = '';
              }
            }}
            style={{ fontSize: '0.9rem' }}
          />
        </div>

        </section>

        <div style={{ marginTop: 32, display: 'flex', alignItems: 'center', gap: '16px' }}>
          <AdminSaveButton type="submit" disabled={saving}>
            {saving ? 'Saving...' : 'Save Home Page Content'}
          </AdminSaveButton>
          {saveSuccess && (
            <span style={{ color: '#1b5e20', fontWeight: '500' }}>{saveSuccess}</span>
          )}
          {error && (
            <span style={{ color: '#b71c1c', fontWeight: '500' }}>{error}</span>
          )}
          {uploadError && (
            <span style={{ color: '#b71c1c', fontWeight: '500' }}>{uploadError}</span>
          )}
        </div>
      </form>
    </>
  );
};

export default AdminHomePage;
